import React, { useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Container,
  Tabs,
  Tab,
  Box,
} from '@material-ui/core';
import { makeStyles, ThemeProvider, useTheme, withStyles } from '@material-ui/core/styles';
import Page from 'src/components/Page';
import LanguageSelector from 'src/components/LanguageSelector';
import claimService from '../../../services/claimService';
import DetailTabContent from './DetailTabContent';
import ErrorAlertIcon from 'src/components/ErrorAlertIcon';
import Header from './Header';
import hasAccess, { ROLES } from 'src/utils/hasAccess';
import { useSelector } from 'react-redux';
import externalClaimsTheme from '../externalClaimTheme';
import { useHistory } from 'react-router-dom';
import { blue } from '@material-ui/core/colors';

const ACTIVITIES_TAB = {
  value: 'activities',
  label: 'glassClaimTabs.activities'
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    paddingBottom: theme.spacing(3),
  },
  card: {
    boxShadow: '1px 1px 10px 1px #0000000d',
    borderRadius: theme.spacing(0.75),
    overflow: 'inherit'
  },
  cardContent: {
    padding: theme.spacing(2, 0),
    height: 'auto',
  },
  docAlert: {
    marginLeft: theme.spacing(1)
  },
  [theme.breakpoints.down(992)]: {
    container: {
      padding: 0
    },
  },
}));

function ClaimDetailsPageInner(props) {
  const {
    classes,
    isLoading,
    handleGoBack,
    handleClaimStatusUpdate,
    isGlassProcessor,
    claimStatus,
    claimDisposition,
    currentTab,
    handleTabsChange,
    tabs,
    createTabLabel,
    claimInformation,
    dealerInformation,
    claimContactInformation,
    claimParts,
    member,
    user,
    fromEmail,
    updateClaim,
    documentData,
    handleSaveNote,
    handleDeleteClaimNote,
    handleUpdateClaimNote,
    claimNotes,
    fromGlassSearchView,
    userName,
    userEmail,
    setUserEmail,
    setUserName,
    claimEvents
  } = props;

  const { t } = useTranslation();

  const StyledTabs = withStyles((theme) => ({
    root: {
      minHeight: 0,
      maxHeight: 35,
      position: 'relative', // Ensure stacking context for indicator
    },
    indicator: {
      backgroundColor: theme.palette.background.paper,
      borderTopRightRadius: 12,
      borderTopLeftRadius: 12,
      height: 35,
      //position: 'relative',
      float: 'left',

      '&:before': {
        position: 'absolute',
        bottom: 0,
        content: '""',
        zIndex: 1,
        backgroundColor: theme.palette.background.paper,
        width: 30,
        height: 20,
        left: -30
      },
      '&:after': {
        position: 'absolute',
        bottom: 0,
        content: '""',
        zIndex: 1,
        backgroundColor: theme.palette.background.paper,
        width: 30,
        height: 20,
        right: -30
      },
    },
    tabShape: {
      padding: '40px 80px 20px 80px',
      position: 'relative',
      borderTopLeftRadius: 30,
      borderTopRightRadius: 30,
      borderBottomLeftRadius: -60,
      borderBottomRightRadius: -60,
      backgroundColor: "#ffffff",
      '&:before': {
        width: 100,
        height: 25,
        marginTop: 4,
        borderRadius: '30px 30px 30px 30px',
        backgroundColor: blue[500],
        zIndex: 2,
        position: 'absolute',
        bottom: 10,
        content: '""',
        left: 40,
        top: 0
      },
      '&:after': {
        width: 100,
        height: 25,
        marginTop: 4,
        borderRadius: '30px 30px 30px 30px',
        backgroundColor: blue[500],
        zIndex: 2,
        position: 'absolute',
        bottom: 10,
        content: '""',
        right: 40,
        top: 0
      }
    },
    highlight: {
      height: 25,
      width: '90%',
      margin: '3px auto',
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.secondary.main,
    }
  }))(({ classes: tabClasses, ...tabsProps }) => (
    <Tabs
      {...tabsProps}
      TabIndicatorProps={{
        children: (
          <>
            <span className={tabClasses.tabShape} />
            <div className={tabClasses.highlight} />
          </>
        )
      }}
    />
  ));

  StyledTabs.propTypes = {
    classes: PropTypes.shape({
      tabShape: PropTypes.string.isRequired,
      highlight: PropTypes.string.isRequired,
    }).isRequired,
  };

  const StyledTab = withStyles((theme) => ({
    root: {
      color: '#8f9092',
      fontWeight: theme.typography.fontWeightMedium,
      marginRight: theme.spacing(1),
      transform: 'translateX(30px)',
      height: 45,
      minHeight: 0,
      transition: 'color 0.3s 0.1s',

      [theme.breakpoints.down(992)]: {
        transform: 'translateX(56px)',
      }
    },
    selected: {
      color: '#fff !important',
      zIndex: 3,
    },
  }))(({ classes, ...props }) => <Tab disableRipple classes={classes} {...props} />);


  return (
    <Page className={classes.root} title={t('glassClaimDetails')}>
      <Header
        isLoading={isLoading}
        onGoBack={handleGoBack}
        handleClaimStatusUpdate={handleClaimStatusUpdate}
        isGlassProcessor={isGlassProcessor}
        fromEmail={fromEmail}
        user={user}
        claimStatus={claimStatus}
        claimDisposition={claimDisposition}
      />
      <Box display="flex" justifyContent="flex-end" px={3} py={1}>
        <LanguageSelector />
      </Box>
      <Container maxWidth="lg" component="section" className={classes.container}>
        <StyledTabs
          onChange={handleTabsChange}
          scrollButtons="auto"
          textColor="secondary"
          value={currentTab}
          variant="scrollable"
        >
          {tabs.map(tab => (
            <StyledTab
              key={tab.value}
              label={createTabLabel(tab)}
              value={tab.value}
            />
          ))}
        </StyledTabs>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <DetailTabContent
              isLoading={isLoading}
              claimInformation={claimInformation}
              dealerInformation={dealerInformation}
              claimContactInformation={claimContactInformation}
              claimParts={claimParts}
              tab={currentTab}
              member={member}
              updateClaim={updateClaim}
              fromGlassSearchView={fromGlassSearchView}
              user={user}
              userEmail={userEmail}
              userName={userName}
              onSetUserEmail={setUserEmail}
              onSetUserName={setUserName}
              isGlassProcessor={isGlassProcessor}
              documentData={documentData}
              handleSaveNote={handleSaveNote}
              handleDeleteClaimNote={handleDeleteClaimNote}
              handleUpdateClaimNote={handleUpdateClaimNote}
              claimNotes={claimNotes}
              claimEvents={claimEvents}
            />
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
}

function ClaimDetailsView({ claimId, onGoBack, onClaimUpdate }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const startTab = location.state?.startTab ?? 'overview';
  const [currentTab, setCurrentTab] = useState(startTab);
  const [localClaim, setLocalClaim] = useState(null);
  const [documentData, setDocumentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dealerInformation, setDealerInformation] = useState({});
  const [claimContactInformation, setClaimContactInformation] = useState({});
  const [claimInformation, setClaimInformation] = useState({});
  const [claimEvents, setClaimEvents] = useState([]);
  const [claimParts, setClaimParts] = useState([]);
  const [claimStatus, setClaimStatus] = useState('Open');
  const { user } = useSelector(state => state.account);
  const [isGlassProcessor, setIsGlassProcessor] = useState(false);
  const [claimNotes, setClaimNotes] = useState([]);
  const [claimDisposition, setClaimDisposition] = useState('In Review');
  const [vendorDealerCode] = useState(location.state?.dealerCode || '');
  const [trackingNumber, setTrackingNumber] = useState(location.state?.trackingNumber || '');
  const [fromGlassSearchView] = useState(location.state?.fromGlassSearchView);
  const fromEmail = new URLSearchParams(location.search).get('fromEmail');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  const history = useHistory();
  const isFromEmail = !!fromEmail;


  const [tabs, setTabs] = useState([
    { value: 'overview', label: 'glassClaimTabs.overview' },
    { value: 'documents', label: 'glassClaimTabs.documents' }
  ]);
  const isNewClaim = location.state?.isNewClaim ?? false;
  const member = {
    id: 0,
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    zipCode2: '',
    dateOfLoss: new Date(),
    causeOfDamage: null
  };

  const handleTabsChange = (_, value) => {
    setCurrentTab(value);
  };

  const getClaimData = async () => {
    try {
      const promises = [
        claimService.getVendorClaim(claimId),
      ];
      const responses = await Promise.all(promises);
      const [{ claim }] = responses;
      const hasEvents = Array.isArray(claim.events) && claim.events.length > 0;

      setTabs((prevTabs) => {
        const hasActivityTab = prevTabs.some(tab => tab.value === ACTIVITIES_TAB.value);
        if (hasEvents === hasActivityTab) return prevTabs;
        return hasEvents
          ? [...prevTabs, ACTIVITIES_TAB]
          : prevTabs.filter(tab => tab.value !== ACTIVITIES_TAB.value);
      });

      //TODO: implement denial reason tab deatail section
      // if (claim.detail?.disposition === 'Denied' && !hasDeniedTab) {
      //   setTabs(tabs => {
      //     tabs.push({ 
      //       value: 'denied',
      //       label: 'Denied Reason'
      //     });
      //     return tabs;
      //   });
      // }

      setLocalClaim(claim);
      setDealerInformation(claim.dealerInformation);
      setClaimContactInformation(claim.claimContactInformation);
      setClaimInformation(claim.claimInformation);
      setClaimInformation((claimInformation) => ({
        ...claimInformation,
        attachments: [],
      }));
      setClaimEvents(claim.events || []);
      setClaimNotes(claim.claimNotes || []);
      setClaimStatus(claim.claimInformation.claimStatus);
      setClaimDisposition(claim.claimInformation.claimDisposition || 'In Review');
      setClaimParts(claim.claimParts);
      setDocumentData(claim.documentData || []);
      if (!fromGlassSearchView && !fromEmail) {
        setUserEmail(claim.claimContactInformation?.emailAddress || '');
        setUserName(claim.claimContactInformation?.contactName || '');
      }
      setTrackingNumber(claim.claimInformation.trackingNumber || '');
    } catch (error) {
      console.error('Failed to load vendor claim', error);
    } finally {
      setIsLoading(false);
    }
  };


  const handleClaimStatusUpdate = async (disposition) => {
    const userFullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    await claimService.updateClaimStatus(claimId, disposition, user?.email, userFullName, user?.id);
    setClaimStatus('Closed');
    setClaimDisposition(disposition);
    getClaimData();
  }


  const handleUpdateClaimNote = async (updatedNote) => {
    const isProcessorNote = true;
    const createdBy = user?.email || 'unknown';
    const userName = `${user?.firstName} ${user?.lastName}`.trim() || 'unknown';
    await claimService.updateClaimNote(claimId, updatedNote.claimNote, updatedNote.vendorClaimNoteId, isProcessorNote, createdBy, user?.id, userName);
    getClaimData();
  }

  const handleDeleteClaimNote = async (deletedNote) => {
    const createdBy = user?.email || 'unknown';
    const userName = `${user?.firstName} ${user?.lastName}`.trim() || 'unknown';
    await claimService.deleteClaimNote(claimId, deletedNote.vendorClaimNoteId, createdBy, user?.id, userName);
    getClaimData();
  }

  /** @param {{value: string, label: string}} tab */
  const createTabLabel = (tab) => {
    const labelText = t(tab.label);
    if (tab.value === 'documents' && localClaim?.isActionNeeded) {
      return (
        <span>
          {labelText}
          <ErrorAlertIcon className={classes.docAlert} />
        </span>
      );
    }
    return labelText;
  }

  const handleGoBack = () => {
    // Always refetch via onGoBack - history.push reuses the same GlassOrderLandingPage instance, whose mount-only effect won't rerun otherwise.
    onGoBack(isNewClaim);
    if (fromGlassSearchView && trackingNumber && vendorDealerCode) {
      history.push({
        pathname: '/glass/view-claim',
        state: {
          userEmail: userEmail,
          userName: userName,
          fromGlassSearchView: fromGlassSearchView,
          dealerCode: vendorDealerCode,
          trackingNumber: trackingNumber,
          isNewClaim: isNewClaim
        }
      });
    }
  }

  const handleSaveNote = async (newClaimNote) => {

    const isProcessorNote = true;
    const createdBy = user?.email || 'unknown';
    const userName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'unknown';
    await claimService.addClaimNote(claimId, newClaimNote, isProcessorNote, createdBy, user?.id, userName);
    getClaimData();
  };

  const handleUpdateLocalClaim = useCallback(async () => {

    await getClaimData();
  }, [claimId]);


  useEffect(() => {
    if (localClaim) {
      const documentCount = localClaim.documentData?.length ?? 0;
      onClaimUpdate({ ...localClaim, claimId, documentCount });
    }
  }, [claimId, localClaim]);


  useEffect(() => {
    (async () => {
      await getClaimData();
      if (user?.roles) {
        const hasGlassProcessorAccess = hasAccess(user?.roles, ROLES.CLAIMS.GLASS_CLAIM_PROCESSOR);
        setIsGlassProcessor(hasGlassProcessorAccess);
      }
    })();
  }, []);



  return (
    <ThemeProvider theme={externalClaimsTheme(theme)}>
      <ClaimDetailsPageInner
        classes={classes}
        isLoading={isLoading}
        handleGoBack={handleGoBack}
        handleClaimStatusUpdate={handleClaimStatusUpdate}
        isGlassProcessor={isGlassProcessor}
        claimStatus={claimStatus}
        claimDisposition={claimDisposition}
        currentTab={currentTab}
        handleTabsChange={handleTabsChange}
        tabs={tabs}
        createTabLabel={createTabLabel}
        claimInformation={claimInformation}
        dealerInformation={dealerInformation}
        claimContactInformation={claimContactInformation}
        claimParts={claimParts}
        user={user}
        member={member}
        fromEmail={isFromEmail}
        documentData={documentData}
        handleSaveNote={handleSaveNote}
        handleDeleteClaimNote={handleDeleteClaimNote}
        handleUpdateClaimNote={handleUpdateClaimNote}
        claimNotes={claimNotes}
        updateClaim={handleUpdateLocalClaim}
        userEmail={userEmail}
        userName={userName}
        setUserEmail={setUserEmail}
        setUserName={setUserName}
        fromGlassSearchView={fromGlassSearchView}
        claimEvents={claimEvents}
      />
    </ThemeProvider>
  );
}

const partShape = PropTypes.shape({
  partNumber: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const claimNoteShape = PropTypes.shape({
  vendorClaimNoteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  claimNote: PropTypes.string,
  createdBy: PropTypes.string,
  dateCreated: PropTypes.string,
});

const roleShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
});

const claimEventShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
});

const documentEntryShape = PropTypes.shape({
  docCategories: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
  })),
  attachments: PropTypes.arrayOf(PropTypes.shape({
    fileName: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
});

const tabShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

ClaimDetailsView.propTypes = {
  claimId: PropTypes.number.isRequired,
  onGoBack: PropTypes.func.isRequired,
  onClaimUpdate: PropTypes.func.isRequired,
};

ClaimDetailsPageInner.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string,
    card: PropTypes.string,
    cardContent: PropTypes.string,
    container: PropTypes.string,
    docAlert: PropTypes.string,
    tabShape: PropTypes.string,
    highlight: PropTypes.string,
  }).isRequired,
  isLoading: PropTypes.bool,
  handleGoBack: PropTypes.func.isRequired,
  handleClaimStatusUpdate: PropTypes.func.isRequired,
  isGlassProcessor: PropTypes.bool,
  claimStatus: PropTypes.string.isRequired,
  claimDisposition: PropTypes.string,
  currentTab: PropTypes.string.isRequired,
  handleTabsChange: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(tabShape).isRequired,
  createTabLabel: PropTypes.func.isRequired,
  claimInformation: PropTypes.shape({
    claimStatus: PropTypes.string,
    claimDisposition: PropTypes.string,
    trackingNumber: PropTypes.string,
  }).isRequired,
  dealerInformation: PropTypes.shape({
    dealerName: PropTypes.string,
    dealerCode: PropTypes.string,
  }).isRequired,
  claimContactInformation: PropTypes.shape({
    contactName: PropTypes.string,
    emailAddress: PropTypes.string,
    phoneNumber: PropTypes.string,
  }).isRequired,
  claimParts: PropTypes.arrayOf(partShape).isRequired,
  member: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    address1: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    roles: PropTypes.arrayOf(roleShape),
  }),
  fromEmail: PropTypes.bool,
  updateClaim: PropTypes.func.isRequired,
  documentData: PropTypes.arrayOf(documentEntryShape).isRequired,
  handleSaveNote: PropTypes.func.isRequired,
  handleDeleteClaimNote: PropTypes.func.isRequired,
  handleUpdateClaimNote: PropTypes.func.isRequired,
  claimNotes: PropTypes.arrayOf(claimNoteShape).isRequired,
  userEmail: PropTypes.string,
  userName: PropTypes.string,
  setUserEmail: PropTypes.func.isRequired,
  setUserName: PropTypes.func.isRequired,
  fromGlassSearchView: PropTypes.bool,
  claimEvents: PropTypes.arrayOf(claimEventShape).isRequired,
};

export default ClaimDetailsView;
